import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, Film, Images, Loader2 } from 'lucide-react';
import {
  assetDisplayName,
  fetchOrgMediaAssets,
  galleryListStatus,
  isImageAsset,
  isVideoAsset,
  type MediaAsset,
} from '../../lib/mediaGallery';
import { toMediaUrl } from '../../lib/mediaUrl';

export const GALLERY_DRAG_TYPE = 'application/x-seu-gallery';

export type GalleryDragKind = 'image' | 'video';

export type GalleryDragItem = { url: string; title: string; kind: GalleryDragKind };

const COLLAPSED_KEY = 'seu.video.telaEmBranco.trayCollapsed';
const MAX_ITEMS = 6;

type Props = {
  orgId: string;
  role?: string | null;
  /** Changes when the board gains media, so fresh uploads show up. */
  refreshKey: number;
  boardUrls: Set<string>;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onAdd: (item: GalleryDragItem) => void;
  onOpenGallery: (kind: GalleryDragKind) => void;
};

export function loadTrayCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function saveTrayCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* storage unavailable */
  }
}

export default function CanvasGalleryTray(props: Props) {
  const { orgId, role, refreshKey, boardUrls, collapsed } = props;
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<GalleryDragKind>('image');

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    fetchOrgMediaAssets(orgId, { assetType: 'all', status: galleryListStatus(role) })
      .then((rows) => {
        if (!cancelled) setAssets(rows);
      })
      .catch(() => {
        if (!cancelled) setAssets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, role, refreshKey]);

  const visible = useMemo(
    () => assets.filter(tab === 'image' ? isImageAsset : isVideoAsset).slice(0, MAX_ITEMS),
    [assets, tab],
  );

  if (collapsed) {
    return (
      <button
        type="button"
        className="tb-tray-pill"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => props.onCollapsedChange(false)}
      >
        <Images size={15} /> Sua galeria
      </button>
    );
  }

  return (
    <div className="tb-tray" onPointerDown={(e) => e.stopPropagation()}>
      <div className="tb-tray-head">
        <strong>Sua galeria</strong>
        <div className="tb-tray-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'image'} className={tab === 'image' ? 'is-active' : ''} onClick={() => setTab('image')}>
            Fotos
          </button>
          <button type="button" role="tab" aria-selected={tab === 'video'} className={tab === 'video' ? 'is-active' : ''} onClick={() => setTab('video')}>
            Vídeos
          </button>
        </div>
        <button type="button" className="tb-tray-collapse" aria-label="Recolher" title="Recolher" onClick={() => props.onCollapsedChange(true)}>
          <ChevronDown size={15} />
        </button>
      </div>
      <div className="tb-tray-hint">arraste para o quadro ou clique para adicionar</div>

      {loading && !assets.length ? (
        <div className="tb-tray-empty">
          <Loader2 size={16} className="spin" />
        </div>
      ) : visible.length ? (
        <div className="tb-tray-grid">
          {visible.map((asset) => {
            const url = toMediaUrl(asset.video_url);
            const thumb = asset.thumbnail_url ? toMediaUrl(asset.thumbnail_url) : '';
            const title = assetDisplayName(asset);
            const onBoard = boardUrls.has(url);
            const item: GalleryDragItem = { url, title, kind: tab };
            return (
              <button
                key={asset.id}
                type="button"
                className={`tb-tray-thumb${onBoard ? ' is-on-board' : ''}`}
                title={onBoard ? `${title} · já está no quadro` : `${title} · arraste ou clique para adicionar`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(GALLERY_DRAG_TYPE, JSON.stringify([item]));
                  e.dataTransfer.setData('text/uri-list', url);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onClick={() => props.onAdd(item)}
              >
                {tab === 'image' || thumb ? (
                  <img src={thumb || url} alt={title} draggable={false} loading="lazy" />
                ) : (
                  <video src={`${url}#t=0.5`} muted playsInline preload="metadata" />
                )}
                {tab === 'video' && (
                  <span className="tb-tray-badge">
                    <Film size={10} />
                  </span>
                )}
                {onBoard && (
                  <span className="tb-tray-check">
                    <Check size={11} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="tb-tray-empty">
          {tab === 'image'
            ? 'Nenhuma foto na galeria ainda. Arraste do computador para o quadro.'
            : 'Nenhum vídeo na galeria ainda.'}
        </div>
      )}

      <button type="button" className="tb-tray-more" onClick={() => props.onOpenGallery(tab)}>
        Ver galeria completa
      </button>
    </div>
  );
}
